import { AlertTriangle, RefreshCw, Sun } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useLocale } from '../context/LocaleContext';

function SolarLoader({ compact = false }: { compact?: boolean }) {
  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center rounded-full border border-sun-200 bg-sun-50 text-sun-700 ${compact ? 'h-10 w-10' : 'h-14 w-14'}`}
      aria-hidden="true"
    >
      <Sun
        className={`${compact ? 'h-5 w-5' : 'h-7 w-7'} motion-safe:animate-[spin_4s_linear_infinite] motion-reduce:animate-none`}
      />
      <span className="absolute inset-1 rounded-full border border-dashed border-sun-300 motion-safe:animate-[spin_7s_linear_infinite_reverse] motion-reduce:animate-none" />
    </span>
  );
}

export function PageLoadingState({ message }: { message?: string }) {
  const { t } = useLocale();
  return (
    <section
      className="container-page flex min-h-[24rem] items-center justify-center py-12 sm:min-h-[30rem]"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center shadow-sm sm:px-10">
        <div className="flex justify-center">
          <SolarLoader />
        </div>
        <p className="mt-5 text-lg font-black text-navy-950">{message || t('loading.page')}</p>
        <p className="mt-2 text-sm leading-6 text-slate-600">{t('loading.wait')}</p>
      </div>
    </section>
  );
}

export function RouteLoadingState({
  message,
  timeoutMs = 12000,
}: {
  message?: string;
  timeoutMs?: number;
}) {
  const { t } = useLocale();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => window.clearTimeout(timer);
  }, [timeoutMs]);

  if (timedOut) {
    return (
      <div className="container-page py-12">
        <RetryableErrorState
          message={t('loading.timeout')}
          onRetry={() => window.location.reload()}
        >
          {t('loading.timeoutBody')}
        </RetryableErrorState>
      </div>
    );
  }

  return <PageLoadingState message={message || t('loading.route')} />;
}

export function SectionLoadingState({
  message,
  rows = 3,
  className = '',
}: {
  message?: string;
  rows?: number;
  className?: string;
}) {
  const { t } = useLocale();
  return (
    <section
      className={`rounded-xl border border-slate-200 bg-white p-5 sm:p-6 ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-center gap-3">
        <SolarLoader compact />
        <div>
          <p className="font-black text-navy-950">{message || t('loading.section')}</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">{t('loading.wait')}</p>
        </div>
      </div>
      <div className="mt-5 grid gap-3" aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => (
          <div
            key={index}
            className="animate-pulse rounded-xl border border-slate-100 p-4 motion-reduce:animate-none"
          >
            <div className="h-4 w-2/5 rounded bg-slate-200" />
            <div className="mt-3 h-3 w-full rounded bg-slate-100" />
            <div className="mt-2 h-3 w-4/5 rounded bg-slate-100" />
          </div>
        ))}
      </div>
    </section>
  );
}

export function ResultsLoadingState({ message }: { message?: string }) {
  const { t } = useLocale();
  return <SectionLoadingState message={message || t('loading.results')} rows={4} />;
}

export function RetryableErrorState({
  message,
  onRetry,
  children,
}: {
  message?: string;
  onRetry: () => void;
  children?: ReactNode;
}) {
  const { t } = useLocale();
  return (
    <section className="rounded-xl border border-red-200 bg-red-50 p-5" role="alert">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" />
        <div className="min-w-0">
          <p className="font-black text-red-950">{message || t('loading.error')}</p>
          {children && <div className="mt-2 text-sm leading-6 text-red-900">{children}</div>}
          <button type="button" className="btn-secondary mt-4" onClick={onRetry}>
            <RefreshCw className="h-4 w-4" /> {t('loading.retry')}
          </button>
        </div>
      </div>
    </section>
  );
}
